import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { motion, AnimatePresence } from 'framer-motion';

const LOGO_URL = '/images/soursweet-logo.png';

// navMap - prefix olmadan URL'ler
const navMap = {
  de: {
    home:        { text: 'Startseite',   url: '/' },
    sour:        { text: 'Sour',         url: '/sour' },
    sweet:       { text: 'Sweet',        url: '/sweet' },
    gallery:     { text: 'Galerie',      url: '/galerie' },
    reservation: { text: 'Reservierung', url: '/reservierung' }
  },
  en: {
    home:        { text: 'Home',        url: '/' },
    sour:        { text: 'Sour',        url: '/sour' },
    sweet:       { text: 'Sweet',       url: '/sweet' },
    gallery:     { text: 'Gallery',     url: '/gallery' },
    reservation: { text: 'Reservation', url: '/reservation' }
  }
};

// Sayfa eşleştirme tablosu
const pageMapping = {
  '/': '/',
  '/startseite': '/',
  '/home': '/',
  '/galerie': '/gallery',
  '/gallery': '/galerie',
  '/kontakt': '/contact',
  '/contact': '/kontakt',
  '/reservierung': '/reservation',
  '/reservation': '/reservierung',
  '/datenschutz': '/privacy',
  '/privacy': '/datenschutz',
  '/agb': '/terms',
  '/terms': '/agb',
  '/impressum': '/imprint',
  '/imprint': '/impressum'
};

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const { language } = useLanguage();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  // Mevcut dile göre nav linklerini al
  const currentNav = navMap[language];
  const navLinks = Object.keys(currentNav).map(key => ({
    id: `nav-${key}`,
    text: currentNav[key].text,
    url: currentNav[key].url
  }));

  const isActive = (url) => location.pathname === url;

  // Dil değiştir - sayfa eşleştirmesi ile
  const switchLanguage = (newLang) => {
    if (newLang === language) return;

    localStorage.setItem('siteLang', newLang);

    const path = window.location.pathname;
    
    // Sour ve Sweet her iki dilde de aynı
    if (path === '/sour' || path === '/sweet') {
      window.location.reload();
      return;
    }
    
    // Sayfa eşleştirmesinden yeni URL'yi al
    const newPath = pageMapping[path] || '/';
    window.location.href = newPath;
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 print:hidden ${isScrolled ? 'bg-amber-50/95 backdrop-blur-md shadow-lg' : 'bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center h-20">
          <Link to={currentNav.home.url} className="flex items-center">
            <img src={LOGO_URL} alt="SourSweet Logo" className="h-40 w-auto mt-4 scale-x-150" style={{filter: 'drop-shadow(0 0 20px rgba(251,146,60,0.8)) drop-shadow(0 0 40px rgba(251,146,60,0.5))'}} />
          </Link>

          <div className="flex-1"></div>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8 mr-24">
            {navLinks.map((link) => (
              <Link
                key={link.id}
                id={link.id}
                to={link.url}
                className={`font-heading text-xl font-bold transition-colors hover:text-lime-500 ${isActive(link.url) ? 'text-lime-500' : (isScrolled ? 'text-emerald-800' : 'text-white')}`}
              >
                {link.text}
              </Link>
            ))}
          </nav>

          {/* Language Buttons */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1">
              <button
                onClick={() => switchLanguage('de')}
                className={`px-3 py-1.5 text-sm font-bold rounded-full transition-all ${language === 'de' ? 'bg-lime-200 text-emerald-900 shadow-[0_0_20px_rgba(251,146,60,0.6)]' : (isScrolled ? 'text-emerald-700 hover:bg-lime-200' : 'text-white/70 hover:bg-lime-200 hover:text-emerald-900')}`}
              >
                DE
              </button>
              <span className={isScrolled ? 'text-emerald-700/40' : 'text-white/40'}>|</span>
              <button
                onClick={() => switchLanguage('en')}
                className={`px-3 py-1.5 text-sm font-bold rounded-full transition-all ${language === 'en' ? 'bg-lime-200 text-emerald-900 shadow-[0_0_20px_rgba(251,146,60,0.6)]' : (isScrolled ? 'text-emerald-700 hover:bg-lime-200' : 'text-white/70 hover:bg-lime-200 hover:text-emerald-900')}`}
              >
                EN
              </button>
            </div>

            <button onClick={() => setIsMenuOpen(!isMenuOpen)} className={`md:hidden ${isScrolled ? 'text-emerald-800' : 'text-white'}`}>
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu - Scroll durumuna göre renk değişimi */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden border-t"
            style={{ 
              backgroundColor: isScrolled ? '#fffbeb' : '#064e3b', 
              borderColor: isScrolled ? '#d9f99d' : '#047857',
              zIndex: 9999 
            }}
          >
            <nav className="flex flex-col px-6 py-4">
              {navLinks.map((link) => (
                <Link
                  key={link.id}
                  id={`mobile-${link.id}`}
                  to={link.url}
                  onClick={() => setIsMenuOpen(false)}
                  className="py-3 text-lg font-bold"
                  style={{ 
                    color: isScrolled 
                      ? (isActive(link.url) ? '#65a30d' : '#064e3b') 
                      : (isActive(link.url) ? '#bef264' : '#fef3c7') 
                  }}
                >
                  {link.text}
                </Link>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
